import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/types/api';
import type {
  PlatformUser,
  SubscriptionPlan,
  PlatformSociety,
  PlatformSubscription,
  PlatformInvoice,
  PlatformAuditLog,
  FeatureFlag,
  SystemHealthMetrics,
  GlobalSettings,
  NotificationProviderConfig,
  PlatformDashboardMetrics,
  SocietyPricingOverride,
  Coupon,
  Referral,
  MarketingCommission,
  MarketingPayout,
  BillingCalculationResult,
  MarketingDashboardData,
  SocietyFeatureMatrixResponse,
  SocietyAddon,
  SocietyCustomization,
  SocietyFeatureOverride,
  SocietyFeatureRequest,
  SocietyAccessControlResponse,
  SocietyAuthSettingsResponse,
  SocietyAccessPreviewResponse,
} from '@/types/platform';

export class PlatformService {
  // Auth
  static async login(payload: { email: string; password: string }) {
    const { data } = await apiClient.post<
      ApiResponse<{
        accessToken: string;
        refreshToken: string;
        user: {
          id: string;
          email: string;
          name: string;
          role: string;
          permissions: string[];
          scope: 'PLATFORM';
          avatar?: string;
        };
      }>
    >('/platform/auth/login', payload);
    return data;
  }

  static async getMe() {
    const { data } = await apiClient.get<ApiResponse<PlatformUser>>('/platform/auth/me');
    return data;
  }

  static async getSystemStatus() {
    const { data } = await apiClient.get<
      ApiResponse<{
        appName: string;
        maintenanceMode: boolean;
        maintenanceMessage: string;
        supportEmail?: string;
        supportPhone?: string;
        defaultCurrency?: string;
      }>
    >('/platform/status');
    return data;
  }

  // Dashboard & Health
  static async getDashboard() {
    const { data } = await apiClient.get<ApiResponse<PlatformDashboardMetrics>>(
      '/platform/dashboard',
    );
    return data;
  }

  static async getSystemHealth() {
    const { data } = await apiClient.get<ApiResponse<SystemHealthMetrics>>(
      '/platform/system-health',
    );
    return data;
  }

  // Societies
  static async listSocieties(params?: {
    page?: number;
    limit?: number;
    status?: string;
    societyType?: string;
    search?: string;
    includeDeleted?: boolean;
  }) {
    const { data } = await apiClient.get<ApiResponse<PlatformSociety[]>>('/platform/societies', {
      params,
    });
    return data;
  }

  static async getSociety(id: string) {
    const { data } = await apiClient.get<ApiResponse<PlatformSociety>>(`/platform/societies/${id}`);
    return data;
  }

  static async createSociety(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<
      ApiResponse<{
        society: PlatformSociety;
        presidentUser: {
          _id: string;
          email: string;
          temporaryPassword?: string;
          activationToken?: string;
        } | null;
      }>
    >('/platform/societies', payload);
    return data;
  }

  static async updateSociety(id: string, payload: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiResponse<PlatformSociety>>(
      `/platform/societies/${id}`,
      payload,
    );
    return data;
  }

  static async suspendSociety(
    id: string,
    payload: { reason: string; reEnableDate?: string | null },
  ) {
    const { data } = await apiClient.post<ApiResponse<PlatformSociety>>(
      `/platform/societies/${id}/suspend`,
      payload,
    );
    return data;
  }

  static async activateSociety(id: string) {
    const { data } = await apiClient.post<ApiResponse<PlatformSociety>>(
      `/platform/societies/${id}/activate`,
    );
    return data;
  }

  static async deleteSociety(id: string, reason: string) {
    const { data } = await apiClient.delete<ApiResponse<PlatformSociety>>(
      `/platform/societies/${id}`,
      { data: { reason } },
    );
    return data;
  }

  static async restoreSociety(id: string) {
    const { data } = await apiClient.post<ApiResponse<PlatformSociety>>(
      `/platform/societies/${id}/restore`,
    );
    return data;
  }

  static async impersonateSociety(id: string, reason: string) {
    const { data } = await apiClient.post<
      ApiResponse<{
        impersonationToken: string;
        society: { id: string; name: string; slug: string };
        session: { id: string; startedAt: string };
      }>
    >(`/platform/societies/${id}/impersonate`, { reason });
    return data;
  }

  static async endImpersonation(sessionId: string) {
    const { data } = await apiClient.post<ApiResponse<{ success: boolean }>>(
      `/platform/societies/impersonate/${sessionId}/end`,
    );
    return data;
  }

  // Plans
  static async listPlans(includeInactive = false) {
    const { data } = await apiClient.get<ApiResponse<SubscriptionPlan[]>>('/platform/plans', {
      params: { includeInactive },
    });
    return data;
  }

  static async getPlan(id: string) {
    const { data } = await apiClient.get<ApiResponse<SubscriptionPlan>>(`/platform/plans/${id}`);
    return data;
  }

  static async createPlan(payload: Partial<SubscriptionPlan>) {
    const { data } = await apiClient.post<ApiResponse<SubscriptionPlan>>('/platform/plans', payload);
    return data;
  }

  static async updatePlan(id: string, payload: Partial<SubscriptionPlan>) {
    const { data } = await apiClient.put<ApiResponse<SubscriptionPlan>>(
      `/platform/plans/${id}`,
      payload,
    );
    return data;
  }

  static async deletePlan(id: string) {
    const { data } = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      `/platform/plans/${id}`,
    );
    return data;
  }

  // Subscriptions & Trials
  static async listSubscriptions(params?: {
    page?: number;
    limit?: number;
    societyId?: string;
    status?: string;
    isTrial?: boolean;
  }) {
    const { data } = await apiClient.get<ApiResponse<PlatformSubscription[]>>(
      '/platform/subscriptions',
      { params },
    );
    return data;
  }

  static async createSubscription(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiResponse<PlatformSubscription>>(
      '/platform/subscriptions',
      payload,
    );
    return data;
  }

  static async cancelSubscription(id: string, reason: string) {
    const { data } = await apiClient.post<ApiResponse<PlatformSubscription>>(
      `/platform/subscriptions/${id}/cancel`,
      { reason },
    );
    return data;
  }

  static async extendTrial(
    societyId: string,
    payload: { additionalDays: number; reason: string },
  ) {
    const { data } = await apiClient.post<ApiResponse<{ success: boolean; newEndDate: string }>>(
      `/platform/trials/${societyId}/extend`,
      payload,
    );
    return data;
  }

  static async convertTrial(
    societyId: string,
    payload: {
      planId: string;
      billingCycle: string;
      amount?: number;
      paymentReference?: string;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<PlatformSubscription>>(
      `/platform/trials/${societyId}/convert`,
      payload,
    );
    return data;
  }

  // Invoices
  static async listInvoices(params?: {
    page?: number;
    limit?: number;
    societyId?: string;
    status?: string;
    search?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<PlatformInvoice[]>>('/platform/invoices', {
      params,
    });
    return data;
  }

  static async createInvoice(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiResponse<PlatformInvoice>>(
      '/platform/invoices',
      payload,
    );
    return data;
  }

  static async markInvoicePaid(id: string, payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiResponse<PlatformInvoice>>(
      `/platform/invoices/${id}/pay`,
      payload,
    );
    return data;
  }

  // Feature Flags
  static async listFeatures() {
    const { data } = await apiClient.get<ApiResponse<FeatureFlag[]>>('/platform/features');
    return data;
  }

  static async toggleFeature(key: string, isEnabled: boolean) {
    const { data } = await apiClient.put<ApiResponse<FeatureFlag>>(
      `/platform/features/${key}`,
      { isEnabled },
    );
    return data;
  }

  // Audit Logs
  static async listAuditLogs(params?: {
    page?: number;
    limit?: number;
    action?: string;
    targetType?: string;
    societyId?: string;
    actorEmail?: string;
    search?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<PlatformAuditLog[]>>(
      '/platform/audit-logs',
      { params },
    );
    return data;
  }

  // Platform Users
  static async listUsers() {
    const { data } = await apiClient.get<ApiResponse<PlatformUser[]>>('/platform/users');
    return data;
  }

  static async createUser(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<
      ApiResponse<{ user: PlatformUser; temporaryPassword?: string }>
    >('/platform/users', payload);
    return data;
  }

  static async updateUser(id: string, payload: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiResponse<PlatformUser>>(
      `/platform/users/${id}`,
      payload,
    );
    return data;
  }

  // Settings & Providers
  static async getSettings() {
    const { data } = await apiClient.get<ApiResponse<GlobalSettings>>('/platform/settings');
    return data;
  }

  static async updateSettings(payload: Partial<GlobalSettings>) {
    const { data } = await apiClient.put<ApiResponse<GlobalSettings>>(
      '/platform/settings',
      payload,
    );
    return data;
  }

  static async listProviders() {
    const { data } = await apiClient.get<ApiResponse<NotificationProviderConfig[]>>(
      '/platform/providers',
    );
    return data;
  }

  static async saveProvider(payload: NotificationProviderConfig) {
    const { data } = await apiClient.post<ApiResponse<NotificationProviderConfig>>(
      '/platform/providers',
      payload,
    );
    return data;
  }

  // Society Types (Manageable by Super Admin)
  static async listSocietyTypes(params?: { includeInactive?: boolean }) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/society-types', {
      params,
    });
    return data;
  }

  static async getSocietyType(code: string) {
    const { data } = await apiClient.get<ApiResponse<any>>(`/platform/society-types/${code}`);
    return data;
  }

  static async createSocietyType(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/society-types', payload);
    return data;
  }

  static async updateSocietyType(code: string, payload: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiResponse<any>>(
      `/platform/society-types/${code}`,
      payload,
    );
    return data;
  }

  static async toggleSocietyTypeStatus(code: string, isActive: boolean) {
    const { data } = await apiClient.patch<ApiResponse<any>>(
      `/platform/society-types/${code}/status`,
      { isActive },
    );
    return data;
  }

  static async deleteSocietyType(code: string) {
    const { data } = await apiClient.delete<ApiResponse<{ success: boolean; message: string }>>(
      `/platform/society-types/${code}`,
    );
    return data;
  }

  // --- BILLING CALCULATION ---
  static async calculateBilling(payload: {
    societyId?: string;
    planId: string;
    billingCycle: string;
    explicitUnitCount?: number;
    couponCode?: string;
    referralCode?: string;
    customDiscountPercent?: number;
  }) {
    const { data } = await apiClient.post<ApiResponse<BillingCalculationResult>>(
      '/platform/billing/calculate',
      payload,
    );
    return data;
  }

  // --- SOCIETY PRICING OVERRIDES ---
  static async listPricingOverrides(params?: { societyId?: string; planId?: string }) {
    const { data } = await apiClient.get<ApiResponse<SocietyPricingOverride[]>>(
      '/platform/pricing-overrides',
      { params },
    );
    return data;
  }

  static async getPricingOverride(id: string) {
    const { data } = await apiClient.get<ApiResponse<SocietyPricingOverride>>(
      `/platform/pricing-overrides/${id}`,
    );
    return data;
  }

  static async createPricingOverride(payload: Partial<SocietyPricingOverride>) {
    const { data } = await apiClient.post<ApiResponse<SocietyPricingOverride>>(
      '/platform/pricing-overrides',
      payload,
    );
    return data;
  }

  static async updatePricingOverride(id: string, payload: Partial<SocietyPricingOverride>) {
    const { data } = await apiClient.put<ApiResponse<SocietyPricingOverride>>(
      `/platform/pricing-overrides/${id}`,
      payload,
    );
    return data;
  }

  static async deletePricingOverride(id: string) {
    const { data } = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      `/platform/pricing-overrides/${id}`,
    );
    return data;
  }

  // --- COUPONS ---
  static async listCoupons(params?: { isActive?: boolean }) {
    const { data } = await apiClient.get<ApiResponse<Coupon[]>>('/platform/coupons', {
      params,
    });
    return data;
  }

  static async getCoupon(id: string) {
    const { data } = await apiClient.get<ApiResponse<Coupon>>(`/platform/coupons/${id}`);
    return data;
  }

  static async createCoupon(payload: Partial<Coupon>) {
    const { data } = await apiClient.post<ApiResponse<Coupon>>('/platform/coupons', payload);
    return data;
  }

  static async updateCoupon(id: string, payload: Partial<Coupon>) {
    const { data } = await apiClient.put<ApiResponse<Coupon>>(`/platform/coupons/${id}`, payload);
    return data;
  }

  static async deleteCoupon(id: string) {
    const { data } = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      `/platform/coupons/${id}`,
    );
    return data;
  }

  static async validateCoupon(payload: {
    code: string;
    societyId?: string;
    planId?: string;
    billingCycle?: string;
    orderAmount: number;
  }) {
    const { data } = await apiClient.post<
      ApiResponse<{
        isValid: boolean;
        discountAmount: number;
        discountType?: string;
        discountValue?: number;
        allowStackingWithReferral?: boolean;
        reason?: string;
      }>
    >('/platform/coupons/validate', payload);
    return data;
  }

  // --- REFERRALS ---
  static async listReferrals(params?: {
    sourceType?: string;
    sourceMarketingPersonId?: string;
    sourceSocietyId?: string;
    status?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<Referral[]>>('/platform/referrals', {
      params,
    });
    return data;
  }

  static async createReferral(payload: Partial<Referral>) {
    const { data } = await apiClient.post<ApiResponse<Referral>>('/platform/referrals', payload);
    return data;
  }

  static async validateReferral(code: string) {
    const { data } = await apiClient.get<
      ApiResponse<{
        isValid: boolean;
        sourceType?: string;
        discountPercent: number;
        commissionPercent?: number;
        sourceName?: string;
        reason?: string;
      }>
    >(`/platform/referrals/validate/${code}`);
    return data;
  }

  // --- MARKETING PERSON STAFF & COMMISSIONS & PAYOUTS ---
  static async listMarketingStaff() {
    const { data } = await apiClient.get<ApiResponse<PlatformUser[]>>('/platform/marketing/staff');
    return data;
  }

  static async createMarketingStaff(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiResponse<PlatformUser>>(
      '/platform/marketing/staff',
      payload,
    );
    return data;
  }

  static async updateMarketingStaff(id: string, payload: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiResponse<PlatformUser>>(
      `/platform/marketing/staff/${id}`,
      payload,
    );
    return data;
  }

  static async listCommissions(params?: {
    marketingPersonId?: string;
    societyId?: string;
    billingPeriod?: string;
    status?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<MarketingCommission[]>>(
      '/platform/marketing/commissions',
      { params },
    );
    return data;
  }

  static async listPayouts(params?: {
    marketingPersonId?: string;
    period?: string;
    status?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<MarketingPayout[]>>(
      '/platform/marketing/payouts',
      { params },
    );
    return data;
  }

  static async getPayout(id: string) {
    const { data } = await apiClient.get<ApiResponse<MarketingPayout>>(
      `/platform/marketing/payouts/${id}`,
    );
    return data;
  }

  static async generatePayout(payload: {
    marketingPersonId: string;
    period: string;
    adjustments?: number;
    notes?: string;
  }) {
    const { data } = await apiClient.post<ApiResponse<MarketingPayout>>(
      '/platform/marketing/payouts/generate',
      payload,
    );
    return data;
  }

  static async markPayoutPaid(
    id: string,
    payload: {
      paymentReference: string;
      paymentMethod?: string;
      paymentDate?: string;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<MarketingPayout>>(
      `/platform/marketing/payouts/${id}/pay`,
      payload,
    );
    return data;
  }

  static async downloadPayoutSlip(id: string): Promise<Blob> {
    const response = await apiClient.get(`/platform/marketing/payouts/${id}/slip`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  // --- MARKETING PORTAL ---
  static async getMarketingPortalDashboard() {
    const { data } = await apiClient.get<ApiResponse<MarketingDashboardData>>(
      '/platform/marketing/portal/dashboard',
    );
    return data;
  }

  static async getMarketingPortalReferrals() {
    const { data } = await apiClient.get<ApiResponse<Referral[]>>(
      '/platform/marketing/portal/referrals',
    );
    return data;
  }

  static async getMarketingPortalCommissions(params?: { status?: string; billingPeriod?: string }) {
    const { data } = await apiClient.get<ApiResponse<MarketingCommission[]>>(
      '/platform/marketing/portal/commissions',
      { params },
    );
    return data;
  }

  static async getMarketingPortalPayouts(params?: { status?: string; period?: string }) {
    const { data } = await apiClient.get<ApiResponse<MarketingPayout[]>>(
      '/platform/marketing/portal/payouts',
      { params },
    );
    return data;
  }

  static async getMarketingPortalProfile() {
    const { data } = await apiClient.get<ApiResponse<PlatformUser>>(
      '/platform/marketing/portal/profile',
    );
    return data;
  }

  static async updateMarketingPortalProfile(payload: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiResponse<PlatformUser>>(
      '/platform/marketing/portal/profile',
      payload,
    );
    return data;
  }

  static async downloadMyPayoutSlip(id: string): Promise<Blob> {
    const response = await apiClient.get(`/platform/marketing/portal/payouts/${id}/slip`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  static async requestSettlement(payload?: { notes?: string }) {
    const { data } = await apiClient.post<ApiResponse<MarketingPayout>>(
      '/platform/marketing/portal/settlements/request',
      payload || {},
    );
    return data;
  }


  // --- SOCIETY FEATURE ACCESS, ADD-ONS & CUSTOMIZATIONS ---
  static async getSocietyFeatureMatrix(societyId: string) {
    const { data } = await apiClient.get<ApiResponse<SocietyFeatureMatrixResponse>>(
      `/platform/societies/${societyId}/features/matrix`,
    );
    return data;
  }

  static async assignAddon(
    societyId: string,
    payload: {
      featureCode: string;
      name?: string;
      description?: string;
      pricingModel?: 'FIXED' | 'PER_UNIT';
      price?: number;
      billingFrequency?: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY' | 'ONE_TIME';
      isComplimentary?: boolean;
      complimentaryReason?: string;
      durationDays?: number;
      notes?: string;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<{ addon: SocietyAddon; invoice?: any }>>(
      `/platform/societies/${societyId}/features/addons`,
      payload,
    );
    return data;
  }

  static async updateAddonStatus(
    societyId: string,
    addonId: string,
    status: 'ACTIVE' | 'PAYMENT_PENDING' | 'EXPIRED' | 'CANCELLED',
  ) {
    const { data } = await apiClient.patch<ApiResponse<{ addon: SocietyAddon }>>(
      `/platform/societies/${societyId}/features/addons/${addonId}`,
      { status },
    );
    return data;
  }

  static async removeAddon(societyId: string, addonId: string) {
    const { data } = await apiClient.delete<ApiResponse<{ addon: SocietyAddon }>>(
      `/platform/societies/${societyId}/features/addons/${addonId}`,
    );
    return data;
  }

  static async saveCustomization(
    societyId: string,
    payload: {
      customizationCode: string;
      featureCode?: string;
      name: string;
      description?: string;
      configuration?: Record<string, unknown>;
      isBillable?: boolean;
      price?: number;
      billingFrequency?: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY' | 'ONE_TIME';
      durationDays?: number;
      notes?: string;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<{ customization: SocietyCustomization; invoice?: any }>>(
      `/platform/societies/${societyId}/features/customizations`,
      payload,
    );
    return data;
  }

  static async removeCustomization(societyId: string, code: string) {
    const { data } = await apiClient.delete<ApiResponse<{ customization: SocietyCustomization }>>(
      `/platform/societies/${societyId}/features/customizations/${code}`,
    );
    return data;
  }

  static async setFeatureOverride(
    societyId: string,
    payload: {
      featureCode: string;
      state: 'ENABLED' | 'DENIED' | 'DEFAULT';
      reason: string;
      durationDays?: number;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<{ override: SocietyFeatureOverride }>>(
      `/platform/societies/${societyId}/features/overrides`,
      payload,
    );
    return data;
  }

  static async removeFeatureOverride(societyId: string, featureCode: string) {
    const { data } = await apiClient.delete<ApiResponse<{ override: null }>>(
      `/platform/societies/${societyId}/features/overrides/${featureCode}`,
    );
    return data;
  }

  static async listFeatureRequests(params?: { status?: string; societyId?: string; limit?: number }) {
    const { data } = await apiClient.get<ApiResponse<SocietyFeatureRequest[]>>(
      '/platform/feature-requests',
      { params },
    );
    return data;
  }

  static async processFeatureRequest(
    id: string,
    payload: {
      action: 'APPROVE' | 'REJECT' | 'QUOTE' | 'FULFILL';
      adminNotes?: string;
      quotedPrice?: number;
      billingFrequency?: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY' | 'ONE_TIME';
      isComplimentary?: boolean;
    },
  ) {
    const { data } = await apiClient.patch<ApiResponse<{ request: SocietyFeatureRequest; invoice?: any }>>(
      `/platform/feature-requests/${id}/process`,
      payload,
    );
    return data;
  }

  static async recordManualPayment(
    invoiceId: string,
    payload: {
      paymentDate: string;
      paymentMethod: string;
      paymentReference?: string;
      notes?: string;
    },
  ) {
    const { data } = await apiClient.post<ApiResponse<{ invoice: PlatformInvoice; entitlementUpdated: boolean }>>(
      `/platform/invoices/${invoiceId}/record-payment`,
      payload,
    );
    return data;
  }

  // --- SUPER ADMIN DATA CLEANUP ---
  static async getDataCleanupPreview(tenantIds?: string[]) {
    const { data } = await apiClient.get<
      ApiResponse<{
        scope: 'ALL' | 'SELECTED';
        selectedSocietiesCount: number;
        totalRecordsExpectedToDelete: number;
        breakdown: Array<{
          collectionName: string;
          count: number;
          category: string;
        }>;
        protectedPlatformSummary: {
          superAdminUsersCount: number;
          platformUsersCount: number;
          subscriptionPlansCount: number;
          featureFlagsCount: number;
          systemSettingsCount: number;
          societyTypesCount: number;
        };
      }>
    >('/platform/data-cleanup/preview', {
      params: tenantIds && tenantIds.length ? { tenantIds } : undefined,
    });
    return data;
  }

  static async executeDataCleanup(payload: {
    mode: 'ALL' | 'SELECTED';
    tenantIds?: string[];
    confirmationPhrase: string;
  }) {
    const { data } = await apiClient.post<
      ApiResponse<{
        jobId: string;
        status: 'SUCCESS' | 'PARTIAL' | 'FAILED';
        scope: 'ALL' | 'SELECTED';
        deletedSocietiesCount: number;
        totalDeletedRecords: number;
        deletedCountsByCollection: Record<string, number>;
        preservedPlatformCounts: {
          superAdminUsers: number;
          platformUsers: number;
          subscriptionPlans: number;
          featureFlags: number;
        };
        startedAt: string;
        completedAt: string;
        performedBy: string;
        errors?: string[];
      }>
    >('/platform/data-cleanup/execute', payload);
    return data;
  }

  // --- SUPER ADMIN SOCIETY ACCESS CONTROL & AUTH METHODS ---
  static async getSocietyAccessControl(societyId: string) {
    const { data } = await apiClient.get<ApiResponse<SocietyAccessControlResponse>>(
      `/platform/societies/${societyId}/access-control`,
    );
    return data;
  }

  static async updateSocietyAccessControl(
    societyId: string,
    payload: {
      features?: Record<string, boolean>;
      approvedFeatures?: Record<string, boolean>;
      requestedFeatures?: Record<string, boolean>;
      changeReason?: string;
      reason?: string;
    },
  ) {
    const { data } = await apiClient.put<ApiResponse<SocietyAccessControlResponse>>(
      `/platform/societies/${societyId}/access-control`,
      payload,
    );
    return data;
  }

  static async getSocietyAuthSettings(societyId: string) {
    const { data } = await apiClient.get<ApiResponse<SocietyAuthSettingsResponse>>(
      `/platform/societies/${societyId}/authentication-settings`,
    );
    return data;
  }

  static async updateSocietyAuthSettings(
    societyId: string,
    payload: {
      allowedMethods?: string[];
      authMethods?: Record<string, boolean>;
      reason?: string;
      changeReason?: string;
    },
  ) {
    const { data } = await apiClient.put<ApiResponse<SocietyAuthSettingsResponse>>(
      `/platform/societies/${societyId}/authentication-settings`,
      payload,
    );
    return data;
  }

  static async getSocietyAccessPreview(societyId: string) {
    const { data } = await apiClient.get<ApiResponse<SocietyAccessPreviewResponse>>(
      `/platform/societies/${societyId}/access-preview`,
    );
    return data;
  }

  static async getPublicSocietyAuthMethods(params?: {
    societyId?: string;
    slug?: string;
    identifier?: string;
  }) {
    const { data } = await apiClient.get<ApiResponse<SocietyAuthSettingsResponse>>(
      '/auth/society-methods',
      { params },
    );
    return data;
  }

  // --- WEBSITE MANAGEMENT ---
  static async getWebsiteConfig() {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/website/config');
    return data;
  }

  static async updateWebsiteConfig(payload: any) {
    const { data } = await apiClient.put<ApiResponse<any>>('/platform/website/config', payload);
    return data;
  }

  static async listWebsiteLeads(params?: { status?: string; search?: string; page?: number; limit?: number }) {
    const { data } = await apiClient.get<ApiResponse<{ leads: any[]; total: number; page: number; totalPages: number }>>(
      '/platform/website/leads',
      { params },
    );
    return data;
  }

  static async updateWebsiteLead(id: string, payload: { status?: string; notes?: string; assignedTo?: string }) {
    const { data } = await apiClient.patch<ApiResponse<any>>(`/platform/website/leads/${id}`, payload);
    return data;
  }

  // --- QA AUTOMATION CENTER ---
  static async getQADashboard() {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/qa/dashboard');
    return data;
  }

  static async listQAModules() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/modules');
    return data;
  }

  static async listQATestCases(params?: { moduleCode?: string; priority?: string; testType?: string; isSmoke?: boolean; search?: string }) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/test-cases', { params });
    return data;
  }

  static async listQARequirements(moduleCode?: string) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/requirements', {
      params: moduleCode ? { moduleCode } : undefined,
    });
    return data;
  }

  static async listQARuns(limit = 20) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/runs', {
      params: { limit },
    });
    return data;
  }

  static async getQARunById(runId: string) {
    const { data } = await apiClient.get<ApiResponse<{ run: any; results: any[] }>>(`/platform/qa/runs/${runId}`);
    return data;
  }

  static async triggerQARun(payload: {
    runType: string;
    moduleCode?: string;
    environment?: string;
    tags?: string[];
    selectedTestCaseIds?: string[];
    executionStrategy?: 'AUTO' | 'SEQUENTIAL' | 'PARALLEL' | 'DEPENDENCY_AWARE';
    maxWorkers?: number;
    browserMode?: 'HEADLESS' | 'HEADED';
    browserEngine?: 'CHROMIUM' | 'FIREFOX' | 'WEBKIT';
    timeoutThresholdSeconds?: number;
    keepFailedTestData?: boolean;
    reRunRunId?: string;
  }) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/runs/trigger', payload);
    return data;
  }

  static async reRunQAFailed(runId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/runs/${runId}/rerun-failed`);
    return data;
  }

  static async cancelQARun(runId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/runs/${runId}/cancel`);
    return data;
  }

  static async listQADefects() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/defects');
    return data;
  }

  static async createQADefect(payload: any) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/defects', payload);
    return data;
  }

  static async updateQADefect(defectId: string, payload: any) {
    const { data } = await apiClient.put<ApiResponse<any>>(`/platform/qa/defects/${defectId}`, payload);
    return data;
  }

  static async verifyQADefect(defectId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/defects/${defectId}/verify`);
    return data;
  }

  static async handoffQADefect(defectId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/defects/${defectId}/handoff`);
    return data;
  }

  // --- Phase 2: Datasets ---
  static async listQADatasets() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/datasets');
    return data;
  }

  static async createQADataset(payload: any) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/datasets', payload);
    return data;
  }

  static async dryRunQADataset(payload: { datasetKey: string }) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/datasets/dry-run', payload);
    return data;
  }

  // --- Phase 2: Schedules ---
  static async listQASchedules() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/schedules');
    return data;
  }

  static async createQASchedule(payload: any) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/schedules', payload);
    return data;
  }

  static async updateQASchedule(scheduleId: string, payload: any) {
    const { data } = await apiClient.put<ApiResponse<any>>(`/platform/qa/schedules/${scheduleId}`, payload);
    return data;
  }

  static async deleteQASchedule(scheduleId: string) {
    const { data } = await apiClient.delete<ApiResponse<any>>(`/platform/qa/schedules/${scheduleId}`);
    return data;
  }

  static async triggerQAScheduleNow(scheduleId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/schedules/${scheduleId}/run-now`);
    return data;
  }

  static async getQAScheduleHistory(scheduleId: string) {
    const { data } = await apiClient.get<ApiResponse<any[]>>(`/platform/qa/schedules/${scheduleId}/history`);
    return data;
  }

  // --- Phase 2: Email Configuration & Dispatch ---
  static async getQAEmailConfig() {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/qa/email/config');
    return data;
  }

  static async updateQAEmailConfig(payload: any) {
    const { data } = await apiClient.put<ApiResponse<any>>('/platform/qa/email/config', payload);
    return data;
  }

  static async dispatchQARunEmail(runId: string, recipients?: string[]) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/runs/${runId}/email`, { recipients });
    return data;
  }

  // --- Phase 2: Coverage Gaps & Run Comparison ---
  static async getQACoverageGaps() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/coverage/gaps');
    return data;
  }

  static async compareQARuns(runIdA: string, runIdB: string) {
    const { data } = await apiClient.get<ApiResponse<any>>(`/platform/qa/runs/compare/${runIdA}/${runIdB}`);
    return data;
  }

  // --- Phase 2: Excel Import Preview & Confirm ---
  static async previewQAExcelImport(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  static async confirmQAExcelImport(file: File, options?: { versionMode?: string }) {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.versionMode) formData.append('versionMode', options.versionMode);
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  static async downloadQATemplate(): Promise<Blob> {
    const response = await apiClient.get('/platform/qa/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  // --- Phase 3: Visual Baselines ---
  static async listQABaselines(params?: { testCaseId?: string; browser?: string; theme?: string; isActive?: boolean }) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/visual/baselines', { params });
    return data;
  }

  static async approveQABaseline(baselineId: string, comment?: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/visual/baselines/${baselineId}/approve`, { comment });
    return data;
  }

  static async rejectQABaseline(baselineId: string, comment?: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/visual/baselines/${baselineId}/reject`, { comment });
    return data;
  }

  static async batchApproveQABaselines(baselineIds: string[], comment?: string) {
    const { data } = await apiClient.post<ApiResponse<{ approvedCount: number }>>('/platform/qa/visual/baselines/batch-approve', {
      baselineIds,
      comment,
    });
    return data;
  }

  // --- Phase 3: Accessibility ---
  static async listQAA11yResults(params?: { runId?: string; impact?: string; ruleId?: string }) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/a11y/results', { params });
    return data;
  }

  // --- Phase 3: Change Impact Analysis ---
  static async analyzeQAImpact(changedFiles: string[]) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/impact/analyze', { changedFiles });
    return data;
  }

  static async runQARecommendedTests(changedFiles: string[], environment?: string) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/impact/run-recommended', {
      changedFiles,
      environment,
    });
    return data;
  }

  // --- Phase 3: Release Quality Gates ---
  static async evaluateQAQualityGate(runId?: string, policy?: any) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/gates/evaluate', { runId, policy });
    return data;
  }

  // --- Phase 3: QA Infrastructure Operational Health ---
  static async getQAInfrastructureHealth() {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/qa/infrastructure/health');
    return data;
  }

  // --- Phase 3: Test Quarantine ---
  static async quarantineQATestCase(testCaseId: string, reason: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/test-cases/${testCaseId}/quarantine`, { reason });
    return data;
  }

  static async unquarantineQATestCase(testCaseId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/test-cases/${testCaseId}/unquarantine`);
    return data;
  }

  // --- Phase 3: Cross Browser Matrix ---
  static async getQACrossBrowserMatrix(runId: string) {
    const { data } = await apiClient.get<ApiResponse<any[]>>(`/platform/qa/runs/${runId}/cross-browser`);
    return data;
  }

  // --- Phase 4: Product Coverage Matrix & Gaps ---
  static async getProductCoverageMatrix(moduleCode?: string) {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/coverage-matrix', {
      params: moduleCode ? { moduleCode } : undefined,
    });
    return data;
  }

  static async getProductCoverageGaps() {
    const { data } = await apiClient.get<ApiResponse<any[]>>('/platform/qa/coverage-gaps');
    return data;
  }

  // --- Phase 4: Release Quality Gate Override Guardrails ---
  static async overrideQAQualityGate(payload: {
    runId: string;
    overrideReason: string;
    riskAssessment?: string;
  }) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/gate/override', payload);
    return data;
  }

  // --- Phase 4: Synthetic Lab Web Vitals & API Performance Audits ---
  static async auditQAPerformance(route?: string) {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/qa/performance/audit', {
      params: route ? { route } : undefined,
    });
    return data;
  }

  // --- Fully Automated Self-Healing QA & Antigravity CLI Repair System ---
  static async getQASelfHealingSummary() {
    const { data } = await apiClient.get<ApiResponse<any>>('/platform/qa/self-healing/summary');
    return data;
  }

  static async triggerQASelfHealingCycle(payload?: { browserMode?: string; runType?: string }) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/self-healing/trigger', payload || {});
    return data;
  }

  static async toggleQASelfHealingPause(isPaused: boolean) {
    const { data } = await apiClient.post<ApiResponse<any>>('/platform/qa/self-healing/toggle-pause', { isPaused });
    return data;
  }

  static async retryQARepairJob(jobId: string) {
    const { data } = await apiClient.post<ApiResponse<any>>(`/platform/qa/self-healing/retry/${jobId}`);
    return data;
  }

  static async updateQASelfHealingConfig(payload: any) {
    const { data } = await apiClient.patch<ApiResponse<any>>('/platform/qa/self-healing/config', payload);
    return data;
  }
}



